import { Button } from '@/components/ui/button'
import type { Flashcard } from '@/types'
import { ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'

interface FlashcardViewerProps {
  content: string
}

function parseFlashcards(content: string): Flashcard[] {
  // Extract JSON from <!-- FLASHCARD_DATA [...] --> comment
  const match = content.match(/<!--\s*FLASHCARD_DATA\s*([\s\S]*?)-->/)
  if (!match?.[1]) return []

  try {
    const parsed = JSON.parse(match[1].trim())
    if (Array.isArray(parsed)) {
      return parsed.filter(
        (item): item is Flashcard =>
          typeof item === 'object' && typeof item.q === 'string' && typeof item.a === 'string',
      )
    }
  } catch {
    // Fall back to parsing Q/A from markdown
  }
  return []
}

function FlashcardCard({
  card,
  isFlipped,
  onFlip,
}: {
  card: Flashcard
  isFlipped: boolean
  onFlip: () => void
}) {
  return (
    <button
      type="button"
      className="relative w-full cursor-pointer perspective-1000 bg-transparent border-0 p-0 text-left"
      onClick={onFlip}
      aria-label={isFlipped ? 'Click to show question' : 'Click to show answer'}
    >
      <div
        className={`relative w-full min-h-[150px] sm:min-h-[200px] transition-transform duration-500 transform-style-preserve-3d ${
          isFlipped ? 'rotate-y-180' : ''
        }`}
        style={{
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          transition: 'transform 0.5s',
        }}
      >
        {/* Front - Question */}
        <div
          className="absolute inset-0 rounded-xl border-2 border-primary/20 bg-card p-3 sm:p-6 flex flex-col items-center justify-center text-center"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">
            Question
          </p>
          <p className="text-base sm:text-lg font-medium leading-relaxed">{card.q}</p>
          <p className="text-xs text-muted-foreground mt-4">Click to reveal answer</p>
        </div>

        {/* Back - Answer */}
        <div
          className="absolute inset-0 rounded-xl border-2 border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-800 p-3 sm:p-6 flex flex-col items-center justify-center text-center"
          style={{
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
          }}
        >
          <p className="text-xs font-medium text-green-600 dark:text-green-400 mb-3 uppercase tracking-wide">
            Answer
          </p>
          <p className="text-sm sm:text-base leading-relaxed">{card.a}</p>
          <p className="text-xs text-muted-foreground mt-4">Click to see question</p>
        </div>
      </div>
    </button>
  )
}

export function FlashcardViewer({ content }: FlashcardViewerProps) {
  const cards = useMemo(() => parseFlashcards(content), [content])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isFlipped, setIsFlipped] = useState(false)

  const handleNext = useCallback(() => {
    setIsFlipped(false)
    setCurrentIndex((prev) => Math.min(prev + 1, cards.length - 1))
  }, [cards.length])

  const handlePrev = useCallback(() => {
    setIsFlipped(false)
    setCurrentIndex((prev) => Math.max(prev - 1, 0))
  }, [])

  const handleReset = useCallback(() => {
    setIsFlipped(false)
    setCurrentIndex(0)
  }, [])

  if (cards.length === 0) {
    return null
  }

  const currentCard = cards[currentIndex]
  if (!currentCard) return null

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          Card {currentIndex + 1} of {cards.length}
        </span>
        <Button variant="ghost" size="sm" onClick={handleReset} className="gap-1">
          <RotateCcw className="h-3 w-3" />
          Reset
        </Button>
      </div>

      {/* Progress dots */}
      <div className="flex justify-center gap-1.5">
        {cards.map((card, i) => (
          <button
            key={card.q}
            type="button"
            className={`w-2 h-2 rounded-full transition-colors ${
              i === currentIndex ? 'bg-primary' : i < currentIndex ? 'bg-primary/40' : 'bg-muted'
            }`}
            onClick={() => {
              setIsFlipped(false)
              setCurrentIndex(i)
            }}
            aria-label={`Go to card ${i + 1}`}
          />
        ))}
      </div>

      <FlashcardCard
        card={currentCard}
        isFlipped={isFlipped}
        onFlip={() => setIsFlipped(!isFlipped)}
      />

      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          onClick={handlePrev}
          disabled={currentIndex === 0}
          className="gap-1"
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleNext}
          disabled={currentIndex === cards.length - 1}
          className="gap-1"
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
